// Floor inference for the Property Map.
//
// Rooms carry a `displayName` (e.g. "A-103", "203", "12") and a `Building`.
// We infer a floor number from the numeric portion of the displayName, with a
// per-room manual override stored in the map layout.

/**
 * Extract the trailing numeric portion of a room's displayName.
 * "A-103" -> 103, "203" -> 203, "Lobby" -> null
 */
export const extractRoomNumber = (displayName) => {
  if (displayName == null) return null;
  const match = String(displayName).match(/(\d+)\s*$/);
  if (!match) return null;
  return Number(match[1]);
};

/**
 * Infer a floor from a room number.
 *  - 3+ digit numbers: hundreds place is the floor (103 -> 1, 1204 -> 12)
 *  - 1-2 digit numbers: treated as floor 1 (ground)
 *  - non-numeric: floor 1
 */
export const inferFloorFromNumber = (roomNumber) => {
  if (roomNumber == null || Number.isNaN(roomNumber)) return 1;
  if (roomNumber >= 100) return Math.floor(roomNumber / 100);
  return 1;
};

/**
 * Resolve the effective floor for a room, honoring a manual override.
 * @param {object} room - room doc (needs displayName)
 * @param {object} floorOverrides - { [roomId]: floorNumber }
 * @param {string} roomKey - key used in overrides (room.id)
 */
export const getRoomFloor = (room, floorOverrides = {}, roomKey) => {
  const key = roomKey ?? room.id;
  if (key != null && floorOverrides[key] != null) {
    return Number(floorOverrides[key]);
  }
  return inferFloorFromNumber(extractRoomNumber(room.displayName));
};

/**
 * Sorted, de-duplicated list of floors present for a set of rooms.
 */
export const getFloorsForRooms = (rooms, floorOverrides = {}) => {
  const floors = new Set();
  rooms.forEach((room) => floors.add(getRoomFloor(room, floorOverrides)));
  return [...floors].sort((a, b) => a - b);
};

/**
 * Sorted, de-duplicated list of building identifiers for a set of rooms.
 * Falls back to "1" to match how CreateProperty seeds the Building field.
 */
export const getBuildingsForRooms = (rooms) => {
  const buildings = new Set();
  rooms.forEach((room) => buildings.add(room.Building != null ? String(room.Building) : "1"));
  return [...buildings].sort((a, b) =>
    String(a).localeCompare(String(b), undefined, { numeric: true })
  );
};
